import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Sparkles, ArrowRight, ArrowLeft, CheckCircle, 
  BookOpen, Headphones, Brain, MessageSquare
} from 'lucide-react';

const PlacementQuiz = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await api.get('/quiz/questions');
      setQuestions(response.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      // Save answers to localStorage and redirect to login
      localStorage.setItem('quiz_answers', JSON.stringify(answers));
      const redirectUrl = window.location.origin + '/quiz';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/quiz/submit', { answers });
      setResult(response.data);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartLevel = async () => {
    if (result) {
      try {
        await api.post('/auth/select-level', { level: result.recommended_level });
        navigate(`/level/${result.recommended_level}`);
      } catch (error) {
        console.error('Error selecting level:', error);
      }
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'grammar': return Brain;
      case 'vocabulary': return BookOpen;
      case 'reading': return MessageSquare;
      case 'listening': return Headphones;
      default: return Brain;
    }
  };

  const progress = ((currentIndex + 1) / questions.length) * 100;
  const currentQuestion = questions[currentIndex];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // Show result page
  if (result) {
    const levelColors = {
      A2: "from-green-400 to-green-600",
      B1: "from-blue-400 to-blue-600",
      B2: "from-purple-400 to-purple-600"
    };

    return (
      <div className="min-h-screen bg-[#FDFCF8]">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-[#FDFCF8]/80 backdrop-blur-md border-b border-border/50">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
                DELF Success Academy
              </span>
            </Link>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>

            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              Quiz Complete!
            </h1>
            <p className="text-muted-foreground mb-8">Here's your recommended level</p>

            {/* Recommended Level Card */}
            <div className={`bg-gradient-to-r ${levelColors[result.recommended_level]} rounded-3xl p-8 text-white mb-8`}>
              <div className="text-6xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                {result.recommended_level}
              </div>
              <p className="text-white/80 text-lg">{result.message}</p>
            </div>

            {/* Score Breakdown */}
            <div className="bg-white rounded-2xl p-6 border border-border/50 mb-8">
              <h3 className="font-bold mb-4">Your Scores</h3>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(result.scores).map(([category, score]) => {
                  const Icon = getCategoryIcon(category);
                  return (
                    <div key={category} className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
                      <Icon className="w-5 h-5 text-primary" />
                      <div className="flex-1">
                        <div className="text-sm capitalize">{category}</div>
                      </div>
                      <div className="font-bold">{score}</div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-4 border-t flex items-center justify-between">
                <span className="font-medium">Total Score</span>
                <span className="text-2xl font-bold text-primary">{result.total_score}/{questions.length}</span>
              </div>
            </div>

            <Button
              onClick={handleStartLevel}
              size="lg"
              className="rounded-full px-10 py-6 text-lg font-bold"
              data-testid="start-preparation-btn"
            >
              Start My Preparation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            <p className="mt-4 text-sm text-muted-foreground">
              or <Link to="/levels" className="text-primary hover:underline">choose a different level</Link>
            </p>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="font-bold">DELF Success Academy</span>
          </Link>
          <span className="text-sm text-muted-foreground">
            Placement Quiz
          </span>
        </div>
        <Progress value={progress} className="h-1 rounded-none" />
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-white rounded-2xl p-8 border border-border/50 shadow-sm"
          >
            {/* Question Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                {(() => {
                  const Icon = getCategoryIcon(currentQuestion?.category);
                  return (
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                  );
                })()}
                <span className="text-sm font-medium capitalize text-muted-foreground">
                  {currentQuestion?.category}
                </span>
              </div>
              <span className="text-sm text-muted-foreground">
                {currentIndex + 1} / {questions.length}
              </span>
            </div>

            {/* Question Text */}
            <h2 className="text-xl font-bold mb-2">{currentQuestion?.text}</h2>
            <p className="text-muted-foreground mb-8">{currentQuestion?.text_fr}</p>

            {/* Options */}
            <RadioGroup
              value={answers[currentQuestion?.question_id] || ''}
              onValueChange={(value) => handleAnswer(currentQuestion?.question_id, value)}
              className="space-y-3"
            >
              {currentQuestion?.options.map((option, index) => (
                <div
                  key={index}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    answers[currentQuestion?.question_id] === option
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => handleAnswer(currentQuestion?.question_id, option)}
                >
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value={option} id={`option-${index}`} />
                    <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer font-medium">
                      {option}
                    </Label>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          {currentIndex === questions.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting || Object.keys(answers).length < questions.length}
              className="rounded-full px-8 font-bold"
              data-testid="submit-quiz-btn"
            >
              {submitting ? 'Submitting...' : 'Get My Result'}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!answers[currentQuestion?.question_id]}
              className="rounded-full"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Question dots */}
        <div className="flex justify-center gap-1.5 mt-6">
          {questions.map((q, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                index === currentIndex
                  ? 'bg-primary'
                  : answers[q.question_id]
                  ? 'bg-green-500'
                  : 'bg-muted'
              }`}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default PlacementQuiz;
